import { productPrice } from "../../utils/productPricing";
import React, { useEffect, useState } from "react";
import { AiFillHeart, AiOutlineShoppingCart } from "react-icons/ai";
import { FaEye } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Ratings from "../Ratings";
import { formatCurrency, fetchExchangeRates } from "../../utils/currencyUtils";
import { Box, Heart, Package, ShoppingBag, Minus } from 'lucide-react';
import SmartImage from "../SmartImage";
import axios from "../../api/api"
import {
  add_to_card,
  messageClear,
  add_to_wishlist,
  get_cart_products,
  get_wishlist_products,
  remove_wishlist,
  delete_card_product,
} from "../../store/reducers/cardReducer";

const FadeInItem = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out transform ${isVisible
        ? "opacity-100 translate-y-0 brightness-100 grayscale-0"
        : "opacity-0 translate-y-12 brightness-50 grayscale"
        }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const FeatureProducts = ({ products }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currencyCode, setCurrencyCode] = useState("NGN");
  const [exchangeLoaded, setExchangeLoaded] = useState(false);
  const { userInfo } = useSelector((state) => state.auth);
  const { successMessage, errorMessage, card_products, wishlist } = useSelector((state) => state.card);

  const isProductInCart = (productId) => {
    if (!card_products) return false;
    return card_products.some(shop =>
      shop.products.some(item => item.productInfo._id === productId)
    );
  };

  const isProductInWishlist = (productId) => {
    if (!wishlist) return false;
    return wishlist.some(item => item.productId === productId);
  };

  const add_card = (id) => {
    const product = products.find(p => p._id === id);
    if (product?.priceOptions?.length) { navigate(`/product/details/${product._id}`); return; }
    const cartItemId = card_products?.flatMap(shop => shop.products).find(item => item.productInfo._id === id)?._id;

    if (cartItemId) {
      dispatch(delete_card_product(cartItemId));
    } else {
      dispatch(
        add_to_card({
          userId: userInfo ? userInfo.id : null,
          quantity: 1,
          productId: id,
          productInfo: product
        })
      );
    }
  };
  useEffect(() => {
    if (successMessage) {
      // toast.success(successMessage);
      dispatch(messageClear());
      if (userInfo) {
        dispatch(get_cart_products(userInfo.id));
        dispatch(get_wishlist_products(userInfo.id));
      } else {
        dispatch(get_cart_products(null));
        dispatch(get_wishlist_products(null));
      }
    }
    if (errorMessage) {
      // toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [errorMessage, successMessage, userInfo, dispatch]);
  useEffect(() => {
    fetchExchangeRates().then(() => setExchangeLoaded(true));
  }, []);

  const add_wishlist = (pro) => {
    const existingWishlistItem = wishlist.find(item => item.productId === pro._id);
    if (existingWishlistItem) {
      dispatch(remove_wishlist(existingWishlistItem._id));
    } else {
      dispatch(
        add_to_wishlist({
          userId: userInfo ? userInfo.id : null,
          productId: pro._id,
          name: pro.name,
          price: pro.price,
          image: pro.images[0],
          discount: pro.discount,
          rating: pro.rating,
          slug: pro.slug,
          currency: pro.currency,
          shopName: pro.shopName
        })
      );
    }
  };



  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const { data } = await axios.get("/account/get-details", {
          withCredentials: true,
        });
        if (data.currency) {
          setCurrencyCode(data.currency);
        }
      } catch (error) {
        // Handle error silently
      }
    };

    if (userInfo && userInfo.id) {
      fetchUserDetails();
    }
  }, []);

  return (
    <div className="w-[85%] sm:w-[90%] flex flex-wrap mx-auto">
      <div className="w-full">
        <div className="text-[18px] text-white font-bold relative pb-[45px]">
          <h2>Top selling products</h2>
        </div>
      </div>
      <div className="w-full grid grid-cols-4 md-lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-2 gap-6 sm:gap-2">
        {products.map((p, i) => (
          <FadeInItem key={i} delay={(i % 6) * 150}>
            <div
              className="bg-[#070707] group transition-all duration-500 hover:shadow-md cursor-pointer"
            >
              <div className="relative overflow-hidden">
                {p.isPromoted && (
                  <div className="flex justify-center items-center absolute text-white py-[2px] px-4 rounded-full bg-gradient-to-r from-purple-600 to-purple-800 font-semibold text-xs right-2 top-2 z-10 shadow-lg">
                    ✨ Promoted
                  </div>
                )}
                {isProductInCart(p._id) && (
                  <div className={`flex justify-center items-center absolute text-white w-[38px] h-[38px] rounded-full bg-[#FFE11B] font-semibold text-xs right-2 z-10 shadow-lg transition-all duration-500 ${p.isPromoted ? 'top-10 mt-2' : 'top-2'}`}>
                    <ShoppingBag size={20} className="text-black" />
                  </div>
                )}
                {p.discount && !p.isPromoted ? (
                  <div className="flex justify-center items-center absolute text-white py-[2px] px-6 rounded-full bg-red-600 font-semibold text-s left-2 top-2">
                    {p.discount}%
                  </div>
                ) : p.discount && p.isPromoted ? (
                  <div className="flex justify-center items-center absolute text-white py-[2px] px-6 rounded-full bg-red-600 font-semibold text-s left-2 top-2">
                    {p.discount}%
                  </div>
                ) : (
                  ""
                )}
                <Link
                  to={p.shopName ? `/${p.shopName}/product/details/${p._id}` : `/product/details/${p._id}`}
                  className=""
                > <SmartImage
                    className="sm:w-full w-full h-[240px] nex_img_cover"
                    src={p.images?.[0]}
                    alt={p.name}
                    width={500}
                  />
                </Link>

                <ul className="flex transition-all duration-700 -bottom-10 justify-center items-center gap-2 absolute w-full group-hover:bottom-3">


                </ul>
              </div>
              <div className="py-3 text-white px-2">
                <div className="p-2 flex justify-between items-center">
                  {/* <div className="text-muted-foreground">{p.category}</div> */}
                  {/* <div className="text-sm  text-gray-300 truncate">
                  {p.brand}
                </div> */}
                  <span className="text-lg sm:text-base font-bold"> {productPrice(p, currencyCode)}</span>
                  {/* Debug: Show currency if not NGN or if explicitly set */}
                  {/* <span className="text-xs text-gray-500 ml-2">({p.currency})</span> */}
                </div>
                <div className="p-2">
                  <h2 className="sm:text-sm truncate">{p.name} ({p.stock})</h2>
                </div>

                {/* <div className="p-2 flex flex-row items-center justify-between gap-2">
                <div className="text-sm sm:text-xs text-gray-400 truncate">
                  {p.brand}
                </div>
                <Link to={`/${p.shopName}`} className="flex items-center text-white nex_badge text-sm sm:text-xs truncate max-w-full">
                  By {p.shopName || 'Unknown Shop'} 
                </Link>
              </div> */}

                <div className="gap-3 p-2">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex">
                      <Ratings ratings={p.rating} />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => add_card(p._id)}
                      className={`${isProductInCart(p._id) ? 'bg-white' : 'bg-white'} flex-1 justify-center flex items-center nex_button_main font-semibold text-black hover:opacity-90 sm:text-sm sm:py-1`}
                    >
                      {isProductInCart(p._id) ? 'Remove' : 'Add to cart'}
                      {/* {isProductInCart(p._id) ? <Minus className="ml-2" size={24} /> : <ShoppingBag className="ml-2" size={24} />} */}
                    </button>
                    <div onClick={() => add_wishlist(p)} className="w-[38px] h-[38px] cursor-pointer rounded-full flex justify-center items-center bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all">
                      <Heart size={20} className={`${isProductInWishlist(p._id) ? 'fill-red-500 text-red-500' : 'text-white'}`} />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </FadeInItem>
        ))}
      </div>
    </div>
  );
};

export default FeatureProducts;
